import type { IHttpService } from 'angular';

import { EventCodes } from '../App/model/event-codes';
import type { HttpService } from '../App/services/http-service';
import type { EventService } from '../App/services/event-service';
import type { AuthenticationService } from '../Auth/mx-authentication-service';
import type { PersonalizationService } from '../App/services/personalization-service';
import { defaultThemeParams, defaultTheme } from './default.theme';
import type { ColorService } from './color.service';
import { Theme, ThemeParametersDTO, ThemeRecord, Variable, ThemeVariable, AvailableThemesDTO, themeVariables, ThemeParameter } from './theme.contracts';

export interface ThemeCache {
	loadThemePromise?: Promise<ThemeParameter[]>;
	standardTheme?: Theme;
	contrastTheme?: Theme;
}
export const themeGetParamsURL = 'api/theme/getParameters?newLook=true';
const DefaultThemeId = 'DefaultTheme';
/**
 * @ngdoc service
 * @name mx.app.ThemeService
 * @module mx.app
 *
 * @description
 * A service provides methods for retrieving parameters from themes
 */
export class ThemeService {
	static readonly $inject = ['mx.shell.Http', 'mx.app.ColorService', '$http', 'mx.SolutionBuilderAgent.PersonalizationService', 'mx.shell.EventService', 'mx.shell.AuthService'];

	private availableThemesPromise?: Promise<ThemeRecord[]>;

	private cache: Map<string, ThemeCache> = new Map();

	private _svgFont?: string;

	private contrastMode?: boolean;

	constructor(
		private readonly http: HttpService,
		private readonly colorService: ColorService,
		private readonly $http: IHttpService,
		private readonly personalizationService: ReturnType<typeof PersonalizationService>,
		private readonly eventService: EventService,
		private readonly authService: AuthenticationService
	) {}

	public async getTheme(themeId: string, contrastMode?: boolean): Promise<Theme> {
		contrastMode ??= (await this.personalizationService.getEnhancedColorContrastMode(themeId)) ?? this.contrastMode;
		return await (contrastMode ? this.getContrastTheme(themeId) : this.getStandardTheme(themeId));
	}

	/**
	 * @ngdoc method
	 * @name mx.app.ThemeService#getCurrentTheme
	 * @async
	 * @description Gets theme object
	 *
	 * @returns {Theme} theme object
	 */
	public async getCurrentTheme(contrastMode?: boolean): Promise<Theme> {
		const currentThemeId = await this.getCurrentThemeId();
		const currentTheme = await this.getTheme(currentThemeId, contrastMode ?? this.contrastMode ?? false);
		this.handleCurrentTheme(currentTheme);
		return currentTheme;
	}

	private getThemeUtils(theme: Theme) {
		const setThemeColor = (color: Variable, value: string) => (theme[color] = value);
		const enhanceColorContrast = (color: Variable, bgColor: Variable, ratio = 4.5) => this.colorService.enhanceColorContrast(theme[color], theme[bgColor], ratio);
		const mixHexColors = (color1: Variable, color2: Variable, mixFactor = 0.5) => this.colorService.mixColors(theme[color1], theme[color2], mixFactor);
		const getContentMixColor = (mixFactor: number) => mixHexColors('content-text-color', 'content-bg-color', mixFactor);
		const bodyMixColor = (mixFactor: number) => mixHexColors('body-text-color', 'body-bg-color', mixFactor);
		const headerMixColor = (mixFactor = 1) => mixHexColors('header-text-color', 'header-bg-color', mixFactor);
		const getProperContrastTextColor = (color: Variable, edge?: number) => this.colorService.getProperContrastTextColor(theme[color], edge);
		const getHSL = (color: Variable) => this.colorService.extractHSL(theme[color]);
		const getSaturation = (color: Variable) => this.colorService.getSaturation(theme[color]);
		const getLightness = (color: Variable) => this.colorService.getLightness(theme[color]);
		const getHue = (color: Variable) => this.colorService.getHue(theme[color]);
		const getBrightness = (color: Variable) => this.colorService.getBrightness(theme[color]);

		return {
			setThemeColor,
			enhanceColorContrast,
			mixHexColors,
			getContentMixColor,
			bodyMixColor,
			headerMixColor,
			getProperContrastTextColor,
			getBrightness,
			getHSL,
			getHue,
			getLightness,
			getSaturation,
		};
	}

	public calculateStandardTheme(theme: Theme): Theme {
		const { setThemeColor, enhanceColorContrast, mixHexColors, getContentMixColor, bodyMixColor, headerMixColor, getProperContrastTextColor, getHSL, getBrightness } =
			this.getThemeUtils(theme);

		theme.isDarkTheme = this.colorService.getLightness(theme['content-text-color']) > this.colorService.getLightness(theme['content-bg-color']);

		const baseLayoutColorEnhancedContrast = enhanceColorContrast('base-layout-color', 'content-bg-color', 5);
		const baseLayoutMixWithContentBg = (weight: number) => this.colorService.mixColors(baseLayoutColorEnhancedContrast, theme['content-bg-color'], weight);

		setThemeColor('supplementary-bg-color', baseLayoutMixWithContentBg(0.04));
		setThemeColor('supplementary-bg-color-enhanced', baseLayoutMixWithContentBg(0.08));
		setThemeColor('supplementary-bg-color-enhanced2', baseLayoutMixWithContentBg(0.12));
		setThemeColor('supplementary-bg-color-10per', baseLayoutMixWithContentBg(0.1));
		setThemeColor('supplementary-bg-color-16per', baseLayoutMixWithContentBg(0.16));
		setThemeColor('content-border-color-supplementary', baseLayoutMixWithContentBg(0.04));

		setThemeColor('content-computed-color_8per', getContentMixColor(0.08));
		setThemeColor('content-computed-color_12per', getContentMixColor(0.12));
		setThemeColor('content-computed-color_16per', getContentMixColor(0.16));
		setThemeColor('content-computed-color_20per', getContentMixColor(0.2));
		setThemeColor('content-computed-color_27per', getContentMixColor(0.27));
		setThemeColor('content-computed-color_36per', getContentMixColor(0.36));
		setThemeColor('content-computed-color_38per', getContentMixColor(0.38));
		setThemeColor('content-computed-color_40per', getContentMixColor(0.4));
		setThemeColor('content-computed-color_67per', getContentMixColor(0.67));
		setThemeColor('content-computed-color_77per', getContentMixColor(0.77));
		setThemeColor('content-computed-color_80per', getContentMixColor(0.8));
		setThemeColor('content-computed-color_92per', getContentMixColor(0.92));

		setThemeColor('content-computed-color1', theme['content-computed-color_8per']);
		setThemeColor('content-computed-color2', theme['content-computed-color_27per']);
		setThemeColor('content-computed-color3', theme['content-computed-color_77per']);

		setThemeColor('active-text-color', enhanceColorContrast('active-color', 'content-bg-color', 2.5));
		setThemeColor('bg-muted-color', theme['content-computed-color_8per']);
		setThemeColor('disabled-text-color', theme['content-computed-color_67per']);

		setThemeColor('muted-text-color', theme['content-computed-color3']);
		setThemeColor('content-border-color', theme.isDarkTheme ? theme['supplementary-bg-color-enhanced2'] : theme['supplementary-bg-color-enhanced']);

		setThemeColor('action-btn-text-color', getProperContrastTextColor('action-btn-color'));
		setThemeColor('header-active-color', enhanceColorContrast('active-color', 'header-bg-color', 2));
		setThemeColor('header-active-text-color', enhanceColorContrast('active-color', 'header-bg-color', 2.5));
		setThemeColor('body-active-color', enhanceColorContrast('active-color', 'body-bg-color', 2));
		setThemeColor('body-active-text-color', enhanceColorContrast('active-color', 'body-bg-color', 2.5));
		setThemeColor(
			'action-btn-color-hover',
			mixHexColors(theme['action-btn-text-color'] === theme['content-text-color'] ? 'content-bg-color' : 'content-text-color', 'action-btn-color', 0.16)
		);
		setThemeColor(
			'action-btn-color-focus',
			mixHexColors(theme['action-btn-text-color'] === theme['content-text-color'] ? 'content-bg-color' : 'content-text-color', 'action-btn-color', 0.64)
		);

		const { saturation: contentTextSaturation, hue: contentTextHue } = getHSL('content-text-color');
		const { saturation: bgTextSaturation, hue: bgTextHue, lightness: bgLightness } = getHSL('content-bg-color');
		setThemeColor('content-text-color-saturation', `${contentTextSaturation}%`);
		setThemeColor('content-text-color-hue', `${contentTextHue.toString()}deg`);
		setThemeColor('content-bg-color-saturation', `${bgTextSaturation}%`);
		setThemeColor('content-bg-color-hue', `${bgTextHue.toString()}deg`);
		setThemeColor('content-bg-color-lightness', `${bgLightness}`);

		setThemeColor('body-muted-text-color', bodyMixColor(0.67));
		setThemeColor('body-computed-color1', bodyMixColor(0.08));
		setThemeColor('body-computed-color2', bodyMixColor(0.27));
		setThemeColor('body-computed-color3', bodyMixColor(0.67));

		setThemeColor('header-computed-color1', headerMixColor(0.08));
		setThemeColor('header-computed-color2', headerMixColor(0.27));
		setThemeColor('header-computed-color3', headerMixColor(0.67));

		// We don't recommend to use these colors.
		// It's better to use `success-text-color`, `info-text-color`, `warning-text-color`, `error-text-color` for texts,
		// or `message-success-bg-color`, `message-info-bg-color`, `message-warning-bg-color`, `message-error-bg-color` for backgrounds.
		setThemeColor('validation-success-color', theme['success-color']);
		setThemeColor('validation-info-color', theme['info-color']);
		setThemeColor('validation-warning-color', theme['warning-color']);
		setThemeColor('validation-error-color', theme['error-color']);

		// We don't recommend to use these colors.
		// It's better to use `success-text-color`, `info-text-color`, `warning-text-color`, `error-text-color` for texts,
		// or `message-success-bg-color`, `message-info-bg-color`, `message-warning-bg-color`, `message-error-bg-color` for backgrounds.
		setThemeColor('success-color', enhanceColorContrast('validation-success-color', 'content-bg-color', 2.25));
		setThemeColor('info-color', enhanceColorContrast('validation-info-color', 'content-bg-color', 2.5));
		setThemeColor('warning-color', enhanceColorContrast('validation-warning-color', 'content-bg-color', 1.6));
		setThemeColor('error-color', enhanceColorContrast('validation-error-color', 'content-bg-color', 3));

		setThemeColor('message-text-color', getProperContrastTextColor('validation-error-color'));
		setThemeColor('message-success-bg-color', enhanceColorContrast('validation-success-color', 'message-text-color', 2.5));
		setThemeColor('message-info-bg-color', enhanceColorContrast('validation-info-color', 'message-text-color', 2.5));
		setThemeColor('message-warning-bg-color', enhanceColorContrast('validation-warning-color', 'message-text-color', 2.5));
		setThemeColor('message-error-bg-color', enhanceColorContrast('validation-error-color', 'message-text-color', 2.5));

		setThemeColor('border-radius', '16px');

		setThemeColor('success-text-color', enhanceColorContrast('validation-success-color', 'content-bg-color', 3));
		setThemeColor('info-text-color', enhanceColorContrast('validation-info-color', 'content-bg-color', 3));
		setThemeColor('warning-text-color', enhanceColorContrast('validation-warning-color', 'content-bg-color', 2));
		setThemeColor('error-text-color', enhanceColorContrast('validation-error-color', 'content-bg-color', 3));

		setThemeColor('error-text-color-hover', mixHexColors('content-text-color', 'error-text-color', 0.165));
		setThemeColor('error-text-color-ripple', mixHexColors('content-text-color', 'error-text-color', 0.33));
		setThemeColor('error-color-near-text', mixHexColors('content-text-color', 'error-text-color', 0.66));
		setThemeColor('error-color-near-bg', mixHexColors('content-bg-color', 'error-text-color', 0.66));

		setThemeColor('faded-color', theme['content-computed-color3']);
		setThemeColor('fadest-color', theme['content-computed-color_27per']);
		setThemeColor('fadest-graphics-color', theme['content-computed-color_27per']);
		setThemeColor('disabled-icon-color', mixHexColors('content-icon-color', 'content-bg-color', 0.5));

		setThemeColor('content-border-color-enhanced', theme['supplementary-bg-color-enhanced2']);

		setThemeColor('content-hint-color', this.colorService.mixColors(theme['content-computed-color_27per'], theme['content-computed-color3'], 0.5));
		setThemeColor('body-border-color', theme.isDarkTheme ? bodyMixColor(0.2) : theme['content-computed-color_8per']);

		setThemeColor('scrollbar-color', getContentMixColor(0.33));
		setThemeColor('scrollbar-color-active', getContentMixColor(0.43));
		setThemeColor('scrollbar-color-dimmed', getContentMixColor(0.16));
		setThemeColor('input-border-color', this.colorService.mixColors(theme['base-layout-color'], getContentMixColor(0.24), 0.12));

		setThemeColor('input-border-color-hover', mixHexColors('input-border-color', 'active-color', 0.5));
		setThemeColor(
			'breadcrumbs-color',
			this.colorService.hslToHexString({
				hue: 0,
				saturation: 0,
				lightness: Math.round(0.7 * this.colorService.getLightness(theme['header-text-color']) + 0.3 * this.colorService.getLightness(theme['header-bg-color'])),
				alpha: 1,
			})
		);

		setThemeColor(
			'unified-search-text-color',
			this.colorService.enhanceColorContrast(
				this.colorService.scaleColor(theme['unified-search-bg-color'], {
					lightness: getBrightness('unified-search-bg-color') > 255 / 2 ? -0.02 : 0.02,
				}),
				theme['unified-search-bg-color'],
				5.5
			)
		);

		setThemeColor('btn-text-color', enhanceColorContrast('btn-color', 'content-bg-color', 4));

		//TODO: this colors should be refactored
		setThemeColor('qf-yellow', enhanceColorContrast('validation-warning-color', 'content-bg-color', 1.6));
		setThemeColor('navigation-config-btn-icon-color', enhanceColorContrast('btn-color', 'header-bg-color', theme.isDarkTheme ? 12 : 3.4));
		setThemeColor('content-icon-color-lightness', this.colorService.getLightness(theme['content-icon-color']).toString());
		setThemeColor(
			'content-widget-view-board-contrast-warning',
			this.colorService.enhanceColorContrast(theme['warning-color'], getProperContrastTextColor('warning-text-color'))
		);

		return theme;
	}

	public calculateContrastTheme(theme: Theme): Theme {
		theme = this.calculateStandardTheme(theme);

		const { setThemeColor, enhanceColorContrast, getHSL } = this.getThemeUtils(theme);

		setThemeColor('active-color', enhanceColorContrast('active-color', 'content-bg-color', 7));
		setThemeColor('header-active-color', enhanceColorContrast('active-color', 'header-bg-color', 5.5));
		setThemeColor('header-active-text-color', enhanceColorContrast('active-color', 'header-bg-color', 7));
		setThemeColor('body-active-color', enhanceColorContrast('active-color', 'body-bg-color', 5.5));
		setThemeColor('body-active-text-color', enhanceColorContrast('active-color', 'body-bg-color', 7));
		setThemeColor('active-text-color', enhanceColorContrast('active-color', 'content-bg-color', 7));

		setThemeColor('btn-color', enhanceColorContrast('btn-color', 'content-bg-color', 5.5));
		setThemeColor('enhanced-contrast-action-btn-color', enhanceColorContrast('action-btn-color', 'content-bg-color', 5.5));
		setThemeColor('action-btn-color', theme['enhanced-contrast-action-btn-color']);
		setThemeColor('action-btn-text-color', this.colorService.getProperContrastTextColor(theme['enhanced-contrast-action-btn-color'], 50));

		setThemeColor('header-text-color', enhanceColorContrast('header-text-color', 'header-bg-color', 7));
		setThemeColor('header-icon-color', enhanceColorContrast('header-icon-color', 'header-bg-color'));

		setThemeColor('content-text-color', enhanceColorContrast('content-text-color', 'content-bg-color', 16));
		setThemeColor('content-icon-color', enhanceColorContrast('content-icon-color', 'content-bg-color', 8));

		setThemeColor('body-text-color', enhanceColorContrast('body-text-color', 'body-bg-color', 16));
		setThemeColor('body-icon-color', enhanceColorContrast('body-icon-color', 'body-bg-color'));

		setThemeColor('palette-color1', enhanceColorContrast('palette-color1', 'content-bg-color', 7));
		setThemeColor('palette-color2', enhanceColorContrast('palette-color2', 'content-bg-color', 5));
		setThemeColor('palette-color3', enhanceColorContrast('palette-color3', 'content-bg-color', 3));
		setThemeColor('palette-color4', enhanceColorContrast('palette-color4', 'content-bg-color', 7));
		setThemeColor('palette-color5', enhanceColorContrast('palette-color5', 'content-bg-color', 5));
		setThemeColor('palette-color6', enhanceColorContrast('palette-color6', 'content-bg-color', 3));
		setThemeColor('palette-color7', enhanceColorContrast('palette-color7', 'content-bg-color', 7));
		setThemeColor('palette-color8', enhanceColorContrast('palette-color8', 'content-bg-color', 5));
		setThemeColor('palette-color9', enhanceColorContrast('palette-color9', 'content-bg-color', 3));
		setThemeColor('palette-color10', enhanceColorContrast('palette-color10', 'content-bg-color', 7));
		setThemeColor('palette-color11', enhanceColorContrast('palette-color11', 'content-bg-color', 5));
		setThemeColor('palette-color12', enhanceColorContrast('palette-color12', 'content-bg-color', 3));

		setThemeColor('success-text-color', enhanceColorContrast('success-text-color', 'content-bg-color', 7));
		setThemeColor('info-text-color', enhanceColorContrast('info-text-color', 'content-bg-color', 7));
		setThemeColor('warning-text-color', enhanceColorContrast('warning-text-color', 'content-bg-color', 7));
		setThemeColor('error-text-color', enhanceColorContrast('error-text-color', 'content-bg-color', 7));

		setThemeColor('success-color', enhanceColorContrast('validation-success-color', 'content-bg-color'));
		setThemeColor('info-color', enhanceColorContrast('validation-info-color', 'content-bg-color'));
		setThemeColor('warning-color', enhanceColorContrast('validation-warning-color', 'content-bg-color'));
		setThemeColor('error-color', enhanceColorContrast('validation-error-color', 'content-bg-color'));

		setThemeColor('message-success-bg-color', enhanceColorContrast('validation-success-color', 'message-text-color', 8));
		setThemeColor('message-info-bg-color', enhanceColorContrast('validation-info-color', 'message-text-color', 8));
		setThemeColor('message-warning-bg-color', enhanceColorContrast('validation-warning-color', 'message-text-color', 8));
		setThemeColor('message-error-bg-color', enhanceColorContrast('validation-error-color', 'message-text-color', 8));

		setThemeColor('faded-color', enhanceColorContrast('muted-text-color', 'content-bg-color', 8));
		setThemeColor('fadest-color', enhanceColorContrast('content-computed-color2', 'content-bg-color'));
		setThemeColor('fadest-graphics-color', enhanceColorContrast('content-computed-color1', 'content-bg-color', 3));
		setThemeColor('disabled-text-color', enhanceColorContrast('disabled-text-color', 'content-bg-color', 5.5));
		setThemeColor('disabled-icon-color', enhanceColorContrast('disabled-icon-color', 'content-bg-color', 3));

		setThemeColor('action-btn-color', enhanceColorContrast('action-btn-color', 'content-bg-color', 7));

		const hslContentBorderColor = getHSL('content-border-color');
		setThemeColor(
			'content-border-color',
			this.colorService.enhanceColorContrast(
				this.colorService.hslToHexString({
					hue: hslContentBorderColor.hue,
					saturation: hslContentBorderColor.saturation * 0.3,
					lightness: hslContentBorderColor.lightness,
					alpha: 1,
				}),
				theme['content-bg-color'],
				3
			)
		);

		const hslSupplementaryBgColorEnhancedColor = getHSL('supplementary-bg-color-enhanced2');
		setThemeColor(
			'content-border-color-enhanced',
			this.colorService.enhanceColorContrast(
				this.colorService.hslToHexString({
					hue: hslSupplementaryBgColorEnhancedColor.hue,
					saturation: hslSupplementaryBgColorEnhancedColor.saturation * 0.3,
					lightness: hslSupplementaryBgColorEnhancedColor.lightness,
					alpha: 1,
				}),
				theme['content-bg-color'],
				3
			)
		);
		setThemeColor('content-hint-color', enhanceColorContrast('content-hint-color', 'content-bg-color', 7));
		setThemeColor('body-border-color', enhanceColorContrast('body-border-color', 'body-bg-color', 3.3));
		setThemeColor('body-muted-text-color', enhanceColorContrast('body-computed-color3', 'body-bg-color', 8));
		setThemeColor('input-border-color', enhanceColorContrast('input-border-color', 'content-bg-color', 3));
		setThemeColor('input-border-color-hover', enhanceColorContrast('input-border-color-hover', 'content-bg-color'));
		setThemeColor('breadcrumbs-color', enhanceColorContrast('breadcrumbs-color', 'header-bg-color', 7));

		setThemeColor('btn-text-color', enhanceColorContrast('btn-text-color', 'content-bg-color', 7));

		setThemeColor('scrollbar-color', enhanceColorContrast('scrollbar-color', 'content-bg-color', 3));
		setThemeColor('scrollbar-color-active', enhanceColorContrast('scrollbar-color-active', 'content-bg-color', 4.5));

		setThemeColor('default-font-color', enhanceColorContrast('default-font-color', 'content-bg-color', 7));

		setThemeColor('enhanced-contrast-3', enhanceColorContrast('content-computed-color1', 'content-bg-color', 3));
		setThemeColor('enhanced-contrast-4_5', enhanceColorContrast('content-computed-color1', 'content-bg-color', 4.5));
		setThemeColor('enhanced-contrast-7', enhanceColorContrast('content-computed-color1', 'content-bg-color', 7));
		setThemeColor('enhanced-contrast-max', enhanceColorContrast('content-computed-color1', 'content-bg-color', 21));
		setThemeColor('enhanced-contrast-active-color-fallback', theme['active-color']);

		return theme;
	}

	private getThemeCache(themeId: string): ThemeCache {
		const cache = this.cache.get(themeId);
		if (cache) {
			return cache;
		} else {
			const cache = {} as ThemeCache;
			this.cache.set(themeId, cache);
			return cache;
		}
	}

	private async getStandardTheme(themeId: string): Promise<Theme> {
		const cache = this.getThemeCache(themeId);

		if (cache.standardTheme) {
			return cache.standardTheme;
		}

		const theme = await this.getBaseThemeFromParameters(themeId);
		cache.standardTheme = this.calculateStandardTheme(theme);
		cache.standardTheme.isContrastTheme = false;

		return cache.standardTheme;
	}

	private async getContrastTheme(themeId: string) {
		const cache = this.getThemeCache(themeId);

		if (cache.contrastTheme) {
			return cache.contrastTheme;
		}

		const theme = await this.getBaseThemeFromParameters(themeId);
		cache.contrastTheme = this.calculateContrastTheme(theme);
		cache.contrastTheme.isContrastTheme = true;

		return cache.contrastTheme;
	}

	public async getBaseThemeFromParameters(themeId: string): Promise<Theme> {
		const baseThemeColors = await this.getBaseTheme(themeId);
		const theme = this.getThemeFromParameters(baseThemeColors);
		theme.id = themeId;

		return theme;
	}

	/**
	 * @ngdoc method
	 * @name mx.app.ThemeService#getCurrentColors
	 * @description Gets theme parameters with extended calculated colors
	 */
	public async getCurrentColors() {
		const theme = await this.getCurrentTheme();
		return this.getThemeColors(theme);
	}

	private getThemeColors(theme: Theme) {
		return Object.entries(theme)
			.filter(([key]) => key !== 'isDarkTheme' && key !== 'isContrastTheme' && key !== 'applicationLogoUrl' && key !== 'id' && key !== 'icons-font-path')
			.map(([Key, Value]) => ({ Key, Value })) as { Key: ThemeVariable; Value: string }[];
	}

	private async getPersonalizedTheme() {
		const [personalizedThemeId, availableThemes] = await Promise.all([this.personalizationService.getUserThemeId(), this.getAvailableThemes()]);
		const theme = availableThemes.find((theme) => theme.id === personalizedThemeId);
		if (!theme && personalizedThemeId) {
			this.personalizationService.saveUserThemeId(undefined);
		}
		return theme;
	}

	private async getServerTheme() {
		const availableThemes = await this.getAvailableThemes();
		return availableThemes.find((theme) => theme.active);
	}

	private handleCurrentTheme(theme: Theme): void {
		document.documentElement.style.colorScheme = theme.isDarkTheme ? 'dark' : 'light';
		localStorage.setItem('mx-theme-is-dark', theme.isDarkTheme.toString());
		localStorage.setItem('mx-current-theme-bg-color', theme['content-bg-color']);
	}

	public async getCurrentThemeId() {
		if (await this.authService.isAuthenticated()) {
			const [personalizedTheme, serverTheme] = await Promise.all([this.getPersonalizedTheme(), this.getServerTheme()]);
			const currentThemeId = personalizedTheme?.id || serverTheme?.id || DefaultThemeId;
			if (typeof this.contrastMode === 'undefined') {
				this.contrastMode = await this.personalizationService.getEnhancedColorContrastMode(currentThemeId);
				if (typeof this.contrastMode === 'undefined') {
					this.contrastMode = serverTheme?.isContrast;
				}
			}
			return currentThemeId;
		}
		return DefaultThemeId;
	}

	private async loadTheme(themeId?: string): Promise<ThemeParameter[]> {
		try {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			const httpResponse = await this.http.get<any>(`${themeGetParamsURL}${themeId && themeId !== DefaultThemeId ? `&themeId=${themeId}` : ''}`);
			if (httpResponse?.error_code) {
				throw new Error(httpResponse?.error_code); // e.g. 'NO_VALID_CERTIFICATE'
			}
			const loadedThemeParameters = httpResponse as ThemeParametersDTO;
			const { errors, themeParameters } = await this.validateTheme(loadedThemeParameters);
			errors.forEach((error) => console.error(error));
			return themeParameters;
		} catch (e) {
			console.error(e);
			return defaultThemeParams;
		}
	}

	public getThemeFromParameters(themeParameters: ThemeParameter[]): Theme {
		return themeParameters.reduce<Theme>((theme, param) => {
			theme[param.Key] = param.Value;
			return theme;
		}, {} as Theme);
	}

	/**
	 * @ngdoc method
	 * @name mx.app.ThemeService#validateTheme
	 * @description Validate theme parameters. All colors are required and should be in hex format
	 */
	public async validateTheme(themeParameters: ThemeParametersDTO): Promise<{ errors: string[]; themeParameters: ThemeParameter[]; isValid: boolean }> {
		const errors: string[] = [];
		const validThemeParameters: ThemeParameter[] = [];
		const colorVariables = new Map<ThemeVariable, boolean>(themeVariables.filter((variable) => variable.includes('color')).map((variableName) => [variableName, false]));

		themeParameters.forEach((param) => {
			if (!colorVariables.has(param.Key)) {
				validThemeParameters.push(param as ThemeParameter);
				return;
			}
			let isValid = false;
			if (typeof param.Value === 'string') {
				const { isRGBA, isRGB } = this.colorService.isHexColor(param.Value);
				isValid = isRGBA || isRGB;
			}
			if (!isValid) {
				errors.push(`Color "${param.Key}" has wrong format ("${param.Value}") in a selected theme. Value "${defaultTheme[param.Key]}" is taken from default system theme.`);
				validThemeParameters.push({ Key: param.Key, Value: defaultTheme[param.Key] });
			} else {
				validThemeParameters.push(param as ThemeParameter);
			}
			colorVariables.set(param.Key, true);
		});
		colorVariables.forEach((isPresent, variableName) => {
			if (!isPresent) {
				errors.push(`Color "${variableName}" is not present in a selected theme. Value "${defaultTheme[variableName]}" is taken from default system theme.`);
				validThemeParameters.push({ Key: variableName, Value: defaultTheme[variableName] });
			}
		});
		return { themeParameters: validThemeParameters, errors, isValid: errors.length === 0 };
	}

	/**
	 * @ngdoc method
	 * @name mx.app.ThemeService#getBaseTheme
	 * @description Gets personalized base theme parameters
	 */
	public async getBaseTheme(themeId?: string) {
		themeId = themeId || (await this.getCurrentThemeId());
		const cache = this.getThemeCache(themeId);
		cache.loadThemePromise = cache.loadThemePromise || this.loadTheme(themeId);
		return await cache.loadThemePromise;
	}

	/**
	 * @ngdoc method
	 * @name mx.app.ThemeService#getContentColorShade
	 * @async
	 * @description Calculates color between `Content-Bg-Color` and `Content-Text-Color` with given proportion
	 * @param {number} mixFactor - Represents mixing proportion. Float number between 0 and 1. 0 is background color, 1 is text color.
	 */
	public async getContentColorShade(mixFactor: number): Promise<string> {
		const themeData = await this.getBaseTheme();
		const contentBgColor = themeData.find((c) => c.Key === 'content-bg-color')?.Value as string;
		const contentTextColor = themeData.find((c) => c.Key === 'content-text-color')?.Value as string;
		return this.colorService.mixColors(contentTextColor, contentBgColor, mixFactor);
	}

	/**
	 * @ngdoc method
	 * @name mx.app.ThemeService#loadSvgIconsFont
	 * @async
	 * @description loads icons font in svg format
	 * @returns {svg} svg font
	 */
	public async loadSvgIconsFont(): Promise<string> {
		if (!this._svgFont) {
			const iconFontPath = (await this.getCurrentTheme())['icons-font-path'] + '.svg';
			this._svgFont = (await this.$http.get(iconFontPath)).data as string;
		}
		return this._svgFont;
	}

	private async loadAvailableThemes(): Promise<ThemeRecord[]> {
		let availableThemes: ThemeRecord[] = [];
		const userThemeId = await this.personalizationService.getUserThemeId();
		let isActiveFound = false;

		try {
			availableThemes = (await this.http.get<AvailableThemesDTO>(`api/theme/available?newLook=true`)).map((theme) => {
				const result = {
					id: theme.ObjectId,
					title: theme.Title,
					active: (userThemeId ? userThemeId === theme.ObjectId : theme.Default) || false,
					isContrast: theme.IsContrast || false,
				};
				isActiveFound ||= result.active;
				return result;
			});
		} catch (e) {
			console.error(e);
		}

		if (!isActiveFound && availableThemes.length > 0) {
			availableThemes[0].active = true;
		}

		availableThemes.sort((theme1, theme2) => theme1.title.localeCompare(theme2.title));
		return availableThemes;
	}

	/**
	 * @ngdoc method
	 * @name mx.app.ThemeService#getAvailableThemes
	 * @description get available themes for the user
	 * @returns {ThemeRecord[]} - array of available themes
	 */
	public async getAvailableThemes(): Promise<ThemeRecord[]> {
		this.availableThemesPromise = this.availableThemesPromise || this.loadAvailableThemes();
		return await this.availableThemesPromise;
	}

	/**
	 * @ngdoc method
	 * @name mx.app.ThemeService#setUserTheme
	 * @description save and apply selected user theme
	 */
	public async setUserTheme(id?: string, isContrast?: boolean) {
		this.contrastMode = (await this.personalizationService.getEnhancedColorContrastMode(id)) ?? isContrast ?? false;
		await this.personalizationService.saveUserThemeId(id);
		this.eventService.dispatchEvent(EventCodes.ThemeChanged, {
			themeId: id,
			contrastMode: this.contrastMode,
		});
		if (id) {
			const currentTheme = await this.getTheme(id, this.contrastMode ?? false);
			this.handleCurrentTheme(currentTheme);
		}
	}

	/**
	 * @ngdoc method
	 * @name mx.app.ThemeService#getContrastMode
	 * @description Returns the current contrast mode state, initializing it from persisted settings if needed
	 * @returns {boolean} current contrast mode
	 */
	public async getContrastMode(): Promise<boolean> {
		await this.getCurrentThemeId(); // ensures this.contrastMode is initialized from storage / server theme
		return this.contrastMode ?? false;
	}

	/**
	 * @ngdoc method
	 * @name mx.app.ThemeService#toggleContrastMode
	 * @description save and apply contrast mode
	 * @returns {boolean} toggled contrast mode
	 */
	public async toggleContrastMode(): Promise<boolean> {
		this.contrastMode = !this.contrastMode;
		const themeId = await this.getCurrentThemeId();
		this.eventService.dispatchEvent(EventCodes.ThemeChanged, {
			themeId,
			contrastMode: this.contrastMode,
		});
		await this.personalizationService.setEnhancedColorContrastMode(themeId, this.contrastMode);
		return this.contrastMode;
	}

	/**
	 * @ngdoc method
	 * @name mx.app.ThemeService#applyCssVariables
	 * @description apply theme to document with css variables and classed
	 */
	public applyCssVariables(id: string, theme: Theme, doc: Document, el?: HTMLElement) {
		const rootClassName = `mx-theme-applied--${id}`;
		const styleTagId = `mx-theme-variables--${id}`;

		el = el || doc.documentElement;
		el.classList.add(rootClassName);

		let styleTag = el.querySelector(`.${rootClassName} > #${styleTagId}`) as HTMLStyleElement | undefined;
		if (!styleTag) {
			styleTag = doc.createElement('style');
			styleTag.id = styleTagId;
			styleTag.type = 'text/css';
			el.appendChild(styleTag);
		}

		styleTag.innerHTML = '';

		const colors = this.getThemeColors(theme);

		colors.sort((c1, c2) => c1.Key.localeCompare(c2.Key));

		const cssVariables = `.${rootClassName} { ${colors.map(({ Key, Value }) => `--mx-${Key}: ${Value};`).join(' ')} } `;
		styleTag.appendChild(doc.createTextNode(cssVariables));

		styleTag.appendChild(
			doc.createTextNode(`.${rootClassName} {
				background-color: var(--mx-content-bg-color);
			 	color: var(--mx-content-text-color);
			}`)
		);

		if (theme['applicationLogoUrl']) {
			let logoUrl = theme['applicationLogoUrl'].replace(/^["']|["']$/g, '');

			if (logoUrl.startsWith('data:image/svg;base64,')) {
				logoUrl = logoUrl.replace('data:image/svg;base64,', 'data:image/svg+xml;base64,');
			}

			styleTag.appendChild(doc.createTextNode(`.${rootClassName} .mx-shell-toolbar--logo { background: url(${logoUrl}) no-repeat center / 114px; }`));
		}

		if (theme['icons-font-path']) {
			const name = `Matrix42_${theme['icons-font-path'].split('/').pop()}`;
			const defaultFontName = 'Matrix42 Icons';
			const cssVariables = `.${rootClassName} { --mx-icons-font-name: ${name}, ${defaultFontName}; } `;
			styleTag.appendChild(doc.createTextNode(cssVariables));
			styleTag.appendChild(
				doc.createTextNode(
					`@font-face {
					font-family: "${name}";
					font-style: normal;
					font-weight: 400;
					src: url(${theme['icons-font-path']}.woff2) format('woff2'),
						url(${theme['icons-font-path']}.woff) format('woff')
				}`
				)
			);
		}

		styleTag.appendChild(
			doc.createTextNode(
				`.${rootClassName} select { background-image: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="292.4" height="292.4"><path fill="${encodeURIComponent(
					theme['content-text-color']
				)}" d="M287 69.4a17.6 17.6 0 0 0-13-5.4H18.4c-5 0-9.3 1.8-12.9 5.4A17.6 17.6 0 0 0 0 82.2c0 5 1.8 9.3 5.4 12.9l128 127.9c3.6 3.6 7.8 5.4 12.8 5.4s9.2-1.8 12.8-5.4L287 95c3.5-3.5 5.4-7.8 5.4-12.8 0-5-1.9-9.2-5.5-12.8z"/></svg>'); }`
			)
		);

		this.getCurrentThemeId().then((currentThemeId) => {
			if (currentThemeId) {
				styleTag.appendChild(
					doc.createTextNode(
						`.${rootClassName} #mx-shell-change-theme-${currentThemeId} { background-color: color-mix(in srgb, var(--mx-active-color) 12%, transparent); }`
					)
				);
			}
		});

		const isDarkBodyTheme = this.colorService.getLuminanace(theme['body-text-color']) > this.colorService.getLuminanace(theme['body-bg-color']);
		const isDarkHeaderTheme = this.colorService.getLuminanace(theme['header-text-color']) > this.colorService.getLuminanace(theme['header-bg-color']);

		el.classList.remove(
			'mx-dark-theme',
			'mx-light-theme',
			'mx-enhanced-contrast',
			'mx-standard-contrast',
			'mx-dark-body-theme',
			'mx-light-body-theme',
			'mx-dark-header-theme',
			'mx-light-header-theme'
		);
		el.classList.add(theme.isDarkTheme ? 'mx-dark-theme' : 'mx-light-theme');
		el.classList.add(theme.isContrastTheme ? 'mx-enhanced-contrast' : 'mx-standard-contrast');
		el.classList.add(isDarkBodyTheme ? 'mx-dark-body-theme' : 'mx-light-body-theme');
		el.classList.add(isDarkHeaderTheme ? 'mx-dark-header-theme' : 'mx-light-header-theme');
	}
}
